import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EditIcon from '@mui/icons-material/Edit';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import { GateStatus, RuleType } from '../../../api/conceptgate';

// Display helper for rule type
const getRuleTypeDisplay = (ruleType) => {
    if (ruleType === RuleType.LLM_AGENT || ruleType === 'LLM_AGENT') {
        return 'AI Rule';
    }
    return 'Static Rule';
};

const RuleItem = ({
    userRule,
    libraryRule,
    validationResult,
    isExpanded,
    onToggle,
    onConfigure,
    onRemove,
    onToggleEnabled,
    isCurrentlyValidating = false,
    // Drag and drop props
    draggable = false,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDragEnd,
    onDrop,
    isDragging = false,
    isDragOver = false,
}) => {
    const ruleName = libraryRule?.name || userRule?.name || 'Unknown Rule';
    const effectiveRuleType = userRule?.ruleType || libraryRule?.ruleType;
    const isLlmRule = effectiveRuleType === RuleType.LLM_AGENT || effectiveRuleType === 'LLM_AGENT';
    const isEnabled = userRule?.enabled !== false;
    const ruleTypeDisplay = getRuleTypeDisplay(effectiveRuleType);

    // Determine status from validation result
    const status = validationResult?.status || (isEnabled ? null : 'DISABLED');

    const getStatusIcon = () => {
        if (!isEnabled) {
            return <PauseCircleIcon className="gate-icon disabled" />;
        }
        if (isCurrentlyValidating) {
            return <CircularProgress size={18} className="gate-icon validating" />;
        }
        if (!status) {
            return <HourglassEmptyIcon className="gate-icon pending" />;
        }
        switch (status) {
            case 'PASSED':
            case GateStatus.PASSED:
                return <CheckCircleIcon className="gate-icon passed" />;
            case 'FAILED':
            case GateStatus.FAILED:
                return <CancelIcon className="gate-icon failed" />;
            case 'SKIPPED':
            case GateStatus.SKIPPED:
                return <PauseCircleIcon className="gate-icon skipped" />;
            default:
                return <HourglassEmptyIcon className="gate-icon pending" />;
        }
    };

    const getStatusClass = () => {
        if (!isEnabled) return 'gate-disabled';
        if (!status) return 'gate-pending';
        switch (status) {
            case 'PASSED':
            case GateStatus.PASSED:
                return 'gate-passed';
            case 'FAILED':
            case GateStatus.FAILED:
                return 'gate-failed';
            case 'SKIPPED':
            case GateStatus.SKIPPED:
                return 'gate-skipped';
            default:
                return 'gate-pending';
        }
    };

    const getMessage = () => {
        if (!isEnabled) return 'Disabled';
        if (validationResult?.message) return validationResult.message;
        return 'Pending validation';
    };

    const hasFix = (status === 'FAILED' || status === GateStatus.FAILED) && validationResult?.suggestedFix;
    const hasMessage = validationResult?.message && isEnabled;
    const showExpand = hasFix || hasMessage;

    // Parse details for LLM reasoning
    let details = null;
    if (validationResult?.detailsJson) {
        try {
            details = typeof validationResult.detailsJson === 'string'
                ? JSON.parse(validationResult.detailsJson)
                : validationResult.detailsJson;
        } catch (e) { }
    }

    return (
        <div
            className={`conceptgate-gate-item ${getStatusClass()} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDragEnd={onDragEnd}
            onDrop={onDrop}
        >
            <div className="conceptgate-gate-header" onClick={showExpand ? onToggle : undefined} title={showExpand ? "Click to expand/collapse" : ""}>
                {draggable && (
                    <div
                        className="gate-drag-handle"
                        title="Drag to reorder"
                    >
                        <DragIndicatorIcon fontSize="small" />
                    </div>
                )}
                <div className="gate-status-icon">
                    {getStatusIcon()}
                </div>
                <div className="gate-info">
                    <div className="gate-name-row">
                        <span className="gate-label">{ruleName}</span>
                        <span className="gate-type-badge">
                            {isLlmRule && <PsychologyIcon fontSize="inherit" />}
                            {ruleTypeDisplay}
                        </span>
                    </div>
                    <span className="gate-message">{getMessage()}</span>
                </div>
                <div className="gate-actions">
                    <Switch
                        size="small"
                        checked={isEnabled}
                        onChange={(e) => {
                            e.stopPropagation();
                            if (onToggleEnabled) onToggleEnabled();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="gate-toggle-switch"
                        title={isEnabled ? "Disable rule" : "Enable rule"}
                    />
                    <button
                        className="gate-action-btn gate-edit-btn"
                        onClick={(e) => { e.stopPropagation(); onConfigure(); }}
                        title="Edit rule"
                    >
                        <EditIcon fontSize="small" />
                    </button>
                    {showExpand && (
                        <span
                            className="gate-expand"
                            title="Show details"
                        >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </span>
                    )}
                    <button
                        className="gate-action-btn gate-remove-btn"
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        title="Remove rule"
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>
            </div>
            {isExpanded && isEnabled && (
                <div className="conceptgate-gate-details">
                    {/* Main Values Display */}
                    {(details?.actual_value || validationResult?.actualValue !== undefined) && (
                        <div className="gate-values">
                            <span className="value-item">
                                <span className="value-label">Actual:</span>
                                <span className="value-num">
                                    {typeof (details?.actual_value || validationResult.actualValue) === 'object'
                                        ? JSON.stringify(details?.actual_value || validationResult.actualValue)
                                        : (details?.actual_value || validationResult.actualValue)}
                                </span>
                            </span>
                            <span className="value-item">
                                <span className="value-label">Threshold:</span>
                                <span className="value-num">
                                    {typeof (details?.threshold_value || validationResult.thresholdValue) === 'object'
                                        ? JSON.stringify(details?.threshold_value || validationResult.thresholdValue)
                                        : (details?.threshold_value || validationResult.thresholdValue)}
                                </span>
                            </span>
                        </div>
                    )}

                    {/* AI Reasoning Section */}
                    {(details?.reasoning || details?.message) && isLlmRule && (
                        <div className="gate-reasoning">
                            <div className="reasoning-header">
                                <PsychologyIcon fontSize="small" />
                                <span>AI Reasoning</span>
                            </div>
                            <p className="reasoning-text">{details?.reasoning || details?.message}</p>
                        </div>
                    )}

                    {/* Facts Section */}
                    {details?.facts && Array.isArray(details.facts) && details.facts.length > 0 && (
                        <div className="gate-sub-section">
                            <div className="sub-section-title">Key Facts</div>
                            <ul className="gate-facts-list">
                                {details.facts.map((fact, i) => <li key={i}>{fact}</li>)}
                            </ul>
                        </div>
                    )}

                    {/* Tools Used Section */}
                    {details?.tools_used && Array.isArray(details.tools_used) && details.tools_used.length > 0 && (
                        <div className="gate-sub-section">
                            <div className="sub-section-title">Data Sources</div>
                            <div className="gate-tools-list">
                                {details.tools_used.map((tool, i) => (
                                    <span key={i} className="gate-tool-badge">
                                        {tool.replace('get', '').replace(/([A-Z])/g, ' $1').trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasFix && (
                        <div className="gate-fix">
                            <LightbulbIcon fontSize="small" className="fix-icon" />
                            <span>{validationResult.suggestedFix}</span>
                        </div>
                    )}

                    {!isLlmRule && hasMessage && !hasFix && (
                        <div className="gate-detail-message">
                            {validationResult.message}
                        </div>
                    )}

                    {/* Raw JSON fallback (dev only or if parse failed) */}
                    {!details && validationResult?.detailsJson && (
                        <div className="gate-raw-details">
                            {validationResult.detailsJson}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RuleItem;
